import { useCallback, useEffect, useMemo, useRef } from "react";

// Marking a word known is a per-click action, so writes are debounced into one
// request rather than one per click.
const DEBOUNCE_MS = 1500;

// Mirrors localStorage up to the account for signed in users. Does nothing when
// stateUrl is null, which is how the anonymous experience stays identical.
//
// Known words are sent as additions, never as the authoritative list - the
// server unions them. Un-knowing a word therefore has to be reported
// explicitly, which is what markRemoved is for.
export default function useLabSync(stateUrl) {
  const timer = useRef(null);
  const removals = useRef([]);
  const latest = useRef(null);

  const send = useCallback(
    (keepalive) => {
      if (!stateUrl || !latest.current) return;

      const token = document.querySelector('meta[name="csrf-token"]');
      const sending = removals.current;
      const body = {
        addKnown: latest.current.known,
        removeKnown: sending,
        settings: latest.current.settings,
      };
      removals.current = [];

      fetch(stateUrl, {
        body: JSON.stringify(body),
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token ? token.getAttribute("content") : "",
        },
        keepalive: Boolean(keepalive),
        method: "PATCH",
      }).catch(() => {
        // A failed sync isn't worth interrupting practice over - localStorage
        // still has everything, and the next push resends the full known set.
        //
        // Removals are not so lucky: addKnown is always the whole set and the
        // server only ever unions, so a dropped removeKnown would leave the
        // word marked known on the account for good. Put them back.
        removals.current = sending.concat(removals.current);
      });
    },
    [stateUrl]
  );

  const push = useCallback(
    (state) => {
      if (!stateUrl) return;

      latest.current = state;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        send(false);
      }, DEBOUNCE_MS);
    },
    [send, stateUrl]
  );

  const markRemoved = useCallback((words) => {
    removals.current = removals.current.concat(words);
  }, []);

  // Don't lose the last few seconds of work to a closed tab.
  useEffect(() => {
    if (!stateUrl) return undefined;

    const flush = () => {
      if (!timer.current) return;
      if (document.visibilityState !== "hidden") return;

      clearTimeout(timer.current);
      timer.current = null;
      send(true);
    };

    document.addEventListener("visibilitychange", flush);
    return () => document.removeEventListener("visibilitychange", flush);
  }, [send, stateUrl]);

  // A fresh object literal here changes identity on every render, so the
  // caller's push effect re-runs constantly and keeps restarting the debounce -
  // during a re-render-heavy stretch the flush never lands.
  return useMemo(() => ({ markRemoved, push }), [markRemoved, push]);
}
